// src/LoginPage.jsx

import { useState } from "react";
import { auth, db } from "./firebase";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  fetchSignInMethodsForEmail,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";

export default function LoginPage({ onLogin, onSignIn }) {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  async function handleForgotPassword(e) {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);

    try {
      // Require an email in the field first
      const trimmed = (email || "").trim();
      if (!trimmed) {
        setError("Enter your email first.");
        return;
      }

      // Check if this email exists in Firebase Auth
      const methods = await fetchSignInMethodsForEmail(auth, trimmed);
      const exists = Array.isArray(methods) && methods.length > 0;

      if (!exists) {
        // You asked to explicitly say when the email doesn't exist
        setError("That email isn’t assigned to any Trelli account.");
        return;
      }

      // Optional: add actionCodeSettings if you want a specific continue URL
      await sendPasswordResetEmail(auth, trimmed, {
        url: "https://app.usetrelli.com/",
        handleCodeInApp: false,
      });

      setNotice("Password reset email sent. Check your inbox for instructions.");
    } catch (err) {
      // Common codes: auth/invalid-email, auth/user-not-found, etc.
      const msg = err?.message || "Failed to send password reset email.";
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);

    // 👇 tell the app we’re in the auth flow so mobile guards won’t kick during MFA
    try { sessionStorage.setItem("trelli:authFlow", "1"); } catch {}

    try {
      if (mode === "login") {
        if (onSignIn) {
          // Let App.jsx handle normal + MFA flows. If MFA is needed,
          // this will throw auth/multi-factor-auth-required and App shows the modal.
          await onSignIn(email, password);
        } else {
          await signInWithEmailAndPassword(auth, email, password);
        }

        // ✅ Login finished (either no MFA or MFA already resolved) — clear the flag
        try { sessionStorage.removeItem("trelli:authFlow"); } catch {}

        // App.jsx will navigate after onAuthStateChanged fires
        return;
      }

      // ----- Sign up flow -----
      const userCred = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCred.user;

      const userRef = doc(db, "users", user.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        await setDoc(userRef, {
          email: user.email,
          createdAt: serverTimestamp(),
          plaidConnected: false,
          role: "primary",
        });
      }

      try { await sendEmailVerification(user); } catch (_) {}

      // For signup we’re not in MFA; clear auth flag if still set
      try { sessionStorage.removeItem("trelli:authFlow"); } catch {}
      onLogin && onLogin();

    } catch (err) {
      // If MFA is required, KEEP the flag set so guards don’t fire mid-MFA.
      if (err?.code === "auth/multi-factor-auth-required") {
        setError("");     // App shows the MFA UI/modal; keep the form calm
        // Note: do NOT remove the flag here
      } else {
        // On other errors, clear the flag so the user can retry
        try { sessionStorage.removeItem("trelli:authFlow"); } catch {}
        setError(err?.message || String(err));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Brand header */}
        <div style={styles.brandRow}>
          <div style={styles.brandText}>
            <div style={styles.brandName}>trelli</div>
            <div style={styles.brandTag}>Stay on budget — before you overspend</div>
          </div>
        </div>

        {/* Title */}
        <h2 style={styles.title}>{mode === "login" ? "Welcome back" : "Create your account"}</h2>

        {/* Error */}
        {error && (
          <div style={styles.errorBox}>{error}</div>
        )}
        {!error && notice && (
          <div style={styles.noticeBox}>{notice}</div>
        )}
        {/* If MFA is happening, there will be no error text; the modal is active */}
        {!error && busy === false && mode === "login" && (
          <div style={{ fontSize: 13, color: "#667085", marginBottom: 8 }}>
            If you’re enrolling or signing in with a code, check your phone for an SMS.
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} style={styles.form}>
          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="email"
            placeholder="you@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={busy}
          />

          <label style={{ ...styles.label, marginTop: 12 }}>Password</label>
          <input
            style={styles.input}
            type="password"
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            placeholder={mode === "login" ? "Your password" : "At least 6 characters"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={busy}
          />

          <div style={{ textAlign: "right", marginTop: 8 }}>
            <button
              type="button"
              onClick={handleForgotPassword}
              style={{ ...styles.linkBtn, fontSize: 13, padding: "8px 6px", lineHeight: "24px" }}
              disabled={busy || mode !== "login"}
              aria-label="Reset password"
            >
              Forgot password?
            </button>
          </div>

          <button type="submit" style={styles.primaryBtn} disabled={busy}>
            {busy ? (mode === "login" ? "Signing in…" : "Creating…") : (mode === "login" ? "Sign in" : "Create account")}
          </button>
        </form>

        {/* Mode toggle + helper text */}
        <div style={styles.footerRow}>
          <span style={{ color: "#555" }}>
            {mode === "login" ? "New to Trelli?" : "Already have an account?"}
          </span>
          <button
            type="button"
            onClick={() => {
              if (mode === "login") {
                window.location.assign("/signup");
              } else {
                // If user somehow got into signup mode here, just return to login UI
                window.location.assign("/");
              }
            }}
            style={styles.linkBtn}
            disabled={busy}
          >
            {mode === "login" ? "Create an account" : "Sign in"}
          </button>
        </div>

        {/* Legal / tiny note */}
        <div style={styles.mutedNote}>
          By continuing, you agree to our Terms and acknowledge our Privacy Policy.
        </div>
      </div>
    </div>
  );
}

/* ---------- styles ---------- */

const TRELLI_GREEN = "#00B074";
const TRELLI_GOLD = "#F4C542";

const styles = {
  container: {
    minHeight: "100svh",
    display: "grid",
    placeItems: "center",
    background:
      "radial-gradient(1200px 600px at 10% -20%, rgba(0,176,116,0.08), transparent 50%), radial-gradient(800px 400px at 110% 120%, rgba(244,197,66,0.06), transparent 40%), #f8faf9",
    padding: "24px",
  },
  card: {
    width: "100%",
    maxWidth: 440,
    background: "#fff",
    borderRadius: 16,
    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
    padding: 24,
  },
  brandRow: {
    display: "flex",
    alignItems: "flex-start",
    marginBottom: 8,
  },
  brandText: { display: "flex", flexDirection: "column" },
  brandName: {
    // lower-case brand mark
    textTransform: "lowercase",
    // brand font stack — if you have a custom font, set it in CSS var --trelliBrandFont
    fontFamily: "var(--trelliBrandFont, Inter, system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, 'Apple Color Emoji','Segoe UI Emoji')",
    fontWeight: 900,
    fontSize: 24,
    letterSpacing: 0.2,
    color: TRELLI_GREEN,
  },
  brandTag: { fontSize: 12, color: "#667085" },
  title: { margin: "8px 0 12px", fontSize: 22 },
  errorBox: {
    border: "1px solid #ef9a9a",
    background: "#ffebee",
    color: "#b71c1c",
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 12,
    fontSize: 14,
  },
  noticeBox: {
    border: "1px solid #b7e1cd",
    background: "#e8f5e9",
    color: "#1b5e20",
    borderRadius: 10,
    padding: "10px 12px",
    marginBottom: 12,
    fontSize: 14,
  },
  form: { display: "flex", flexDirection: "column" },
  label: { fontSize: 13, color: "#444", marginBottom: 6, fontWeight: 600 },
  input: {
    height: 46,
    borderRadius: 10,
    border: "1px solid #dce1e5",
    padding: "0 12px",
    outline: "none",
    fontSize: 16,
    transition: "box-shadow .15s ease, border-color .15s ease",
    background: "#fff",
  },
  primaryBtn: {
    marginTop: 16,
    height: 44,
    border: "none",
    borderRadius: 12,
    background: TRELLI_GREEN,
    color: "#fff",
    fontWeight: 800,
    fontSize: 15,
    cursor: "pointer",
    boxShadow: "0 4px 10px rgba(0,176,116,0.25)",
  },
  footerRow: {
    marginTop: 12,
    display: "flex",
    gap: 8,
    alignItems: "center",
    justifyContent: "center",
    fontSize: 14,
  },
  linkBtn: {
    border: "none",
    background: "transparent",
    color: TRELLI_GREEN,
    fontWeight: 700,
    cursor: "pointer",
    textDecoration: "underline",
  },
  mutedNote: {
    marginTop: 8,
    fontSize: 12,
    color: "#667085",
    textAlign: "center",
  },
};